import {
  ChangeLog,
  ActivityItem,
  customAttributeFieldNames,
  dateFieldNames,
  statusUpdateFields,
} from "components/activity-logs/activity-log/definitions";

export const isDateField = (attributeName: string): boolean => {
  const cleanName = attributeName.replace("custom_", "");

  return (
    dateFieldNames.includes(cleanName) ||
    cleanName.endsWith("Date") ||
    cleanName.endsWith("_date")
  );
};

export const parseCustomAttributes = (
  value: string,
): Record<string, string> => {
  if (!value) return {};
  // Parse the format: {key1=value1, key2=value2, ...}
  const cleanValue = value.replace(/^{|}$/g, ""); // Remove outer braces
  if (!cleanValue) return {};

  const result: Record<string, string> = {};
  const regex = /(\w+)=([^,]*(?:,[^,]*)*?)(?=,\s*\w+=|$)/g;

  let match;
  while ((match = regex.exec(cleanValue)) !== null) {
    const key = match[1].trim();
    const value = match[2].trim();
    if (key && value !== undefined) {
      result[key] = value;
    }
  }

  return result;
};

/**
 * Checks if a change log represents a status-related change
 * Handles both direct status fields and fields in custom attributes data map
 */
export const isStatusRelatedChange = (
  change: ChangeLog,
  statusUpdateFields: string[],
): boolean => {
  if (statusUpdateFields.includes(change.attributeName)) {
    return true;
  }

  if (customAttributeFieldNames.includes(change.attributeName)) {
    const oldAttributes = parseCustomAttributes(change.oldValue);
    const newAttributes = parseCustomAttributes(change.newValue);

    return statusUpdateFields.some(
      (field) => oldAttributes[field] !== newAttributes[field],
    );
  }

  return false;
};

// UI Field Mapping
export const AUDIT_FIELDS = {
  status: "Status",
  statusActionOwner: "Action pending",
  statusDetail: "Status detail",
  progressModifiedAt: "Progress updated",
  progressModifiedBy: "Progress updated by",
  country: "Country / Region",
  entity: "Entity",
  taxType: "Tax type",
  taxNumber: "Tax number",
  governmentEntity: "Tax authority",
  auditNumber: "Audit number",
  auditStartDate: "Audit period start",
  auditEndDate: "Audit period end",
  auditInitiationDate: "Audit initiation date",
  statuteOfLimitations: "Statute of limitations",
  auditCloseDate: "Audit close date",
  owner: "Owner",
  description: "Description",
  auditName: "Audit name",
  finalAssessments: "Final assessments",
  totalPayments: "Total payments",
  customAttributesDataMap: "Custom attributes",
} as const;

export const TASK_FIELD_MAPPINGS = {
  status: "Status",
  inquiryType: "Type",
  taskGroupType: "Type",
  actualStartDate: "Actual start",
  actualCompletionDate: "Completion date",
  owners: "Owner",
  subInquiryType: "Subtype",
  dueDate: "Task due date",
  complexity: "Complexity",
  taskGroupName: "Task group",
  plannedStartDate: "Planned start",
  dueIn: "Due in",
  priority: "Priority",
  description: "Description",
  ratio: "Progress",
  taskName: "Task name",
  taskType: "Task type",
  isMilestone: "Milestone",
  jurisdictionLevel: "Jurisdiction level",
  reopenReason: "Reopen reason",
  reopenedBy: "Reopened by",
  reopenedAt: "Reopened at",
  statusChangeReason: "Reason",
  parentTaskId: "Parent task",
};

export const GENERAL_FIELD_MAPPINGS = {
  createdAt: "Created at",
  createdBy: "Created by",
  deleted: "Deleted",
  shortId: "Short ID",
};

export const PAYMENT_ASSESSMENT_FIELD_MAPPINGS = {
  dateReceived: "Date received",
  notes: "Notes",
  paymentAuthorizationLink: "Payment authorization link",
  paymentDate: "Payment date",
  paymentRefNumber: "Payment reference number",
  totalInterestAmount: "Total interest amount",
  totalOtherAmount: "Total other amount",
  totalPenaltyAmount: "Total penalty amount",
  totalTaxAmount: "Total tax amount",
  trackingRefNumber: "Tracking reference number",
  type: "Type",
};

export const GAIA_OBJECT_FIELDS = {
  // Direct gaia object fields
  gaiaobject_title: "Name",
  gaiaobject_title_suffix: "Name suffix",
  gaiaobject_category: "Category",
  short_id: "Short ID",
  modified_at: "Modified at",
  created_at: "Created at",

  // Custom attribute fields (these appear in customAttributesDataMap)
  owner: "Owner",
  severity: "Severity",
  country: "Country",
  actualDueDate: "Actual due date",
  statusActionOwner: "Action pending",
  taxPeriodEnd: "Tax period end",
  taxPeriodStart: "Tax period start",
  description: "Description",
  noticeType: "Notice type",
  effort: "Effort",
  statusDetail: "Status detail",
  noticeId: "Notice ID",
  closeDate: "Close date",
  noticeDate: "Notice date",
  entity: "Entity",
  taxType: "Tax type",
  ResponseDeadline: "Response deadline",
  status: "Status",

  // Litigation specific fields
  contestedAmount: "Contested amount",
  litigationTracker: "Litigation tracker",
  initiationDate: "Initiation date",
  referenceNumber: "Reference number",
  litigationType: "Litigation type",
  outsideCounsel: "Outside counsel",
  savings: "Savings",
  taxAuthority: "Tax authority",
  controversyMemo: "Controversy memo",
  publicDecision: "Public decision",
  litigationTrackerId: "Litigation tracker ID",
  liabilityReporting: "Liability reporting",
  statuteOfLimitations: "Statute of limitations",
  closedDate: "Closed date",
  goForward: "Go forward",
  poaRequired: "POA required",
  initialFilingDate: "Initial filing date",
  caseNumber: "Case number",
  filingDate: "Filing date",
  courtName: "Court name",
} as const;

export const ESCALATION_FIELD_MAPPINGS = {
  notify_at: "Notification date",
  recipients: "Recipients",
  notified: "Notified",
  team_name: "Team",
  type: "Type",
} as const;

export const REVIEW_FIELD_MAPPINGS = {
  reviewStatus: "Status",
  review_status: "Status",
  reviewer: "Reviewer",
  reviewers: "Reviewers",
  requestedBy: "Requested by",
  requested_by: "Requested by",
  dueDate: "Due date",
  due_date: "Due date",
  reviewDate: "Review date",
  review_date: "Review date",
  comments: "Comments",
  messageForReviewers: "Message for reviewers",
  message_for_reviewers: "Message for reviewers",
  deleted: "Deleted",
  reviewType: "Review type",
  review_type: "Review type",
  approvalId: "Approval ID",
  approval_id: "Approval ID",
  ccUsers: "CC users",
  cc_users: "CC users",
  approvedDocCount: "Approved documents",
  approved_doc_count: "Approved documents",
  rejectedDocCount: "Rejected documents",
  rejected_doc_count: "Rejected documents",
} as const;

export type AuditFieldKey = keyof typeof AUDIT_FIELDS;
export type TaskFieldKey = keyof typeof TASK_FIELD_MAPPINGS;
export type GeneralFieldKey = keyof typeof GENERAL_FIELD_MAPPINGS;
export type PaymentAssessmentFieldKey =
  keyof typeof PAYMENT_ASSESSMENT_FIELD_MAPPINGS;
export type GaiaObjectFieldKey = keyof typeof GAIA_OBJECT_FIELDS;
export type EscalationFieldKey = keyof typeof ESCALATION_FIELD_MAPPINGS;
export type ReviewFieldKey = keyof typeof REVIEW_FIELD_MAPPINGS;

/**
 * Prettifies a camelCase string into a human-readable label.
 * Handles acronyms correctly by keeping consecutive uppercase letters together
 * and restoring known acronyms to uppercase.
 * Example: "taskGroupName" → "Task group name", "atlasKBName" → "Atlas KB name"
 */
const KNOWN_ACRONYMS = ["id", "kb", "url"];

export const prettifyCamelCase = (fieldName: string): string => {
  return fieldName
    .replace(/([A-Z]+)([A-Z][a-z0-9])/g, "$1 $2")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase()
    .replace(/^./, (str) => str.toUpperCase())
    .replace(new RegExp(`\\b(${KNOWN_ACRONYMS.join("|")})\\b`, "gi"), (match) =>
      match.toUpperCase(),
    );
};

export const getUIFieldName = (
  dbFieldName: string,
  category: string,
): string => {
  if (dbFieldName.startsWith("custom_")) {
    const cleanFieldName = dbFieldName.replace("custom_", "");
    // Check if it's a known gaia object field first
    if (GAIA_OBJECT_FIELDS[cleanFieldName as GaiaObjectFieldKey]) {
      return GAIA_OBJECT_FIELDS[cleanFieldName as GaiaObjectFieldKey];
    }
    // Otherwise format it nicely
    return prettifyCamelCase(cleanFieldName);
  }

  const snakeToCamel = (str: string) =>
    str
      .toLowerCase()
      .replace(/([-_][a-z])/g, (group) =>
        group.toUpperCase().replace("-", "").replace("_", ""),
      );

  // Check gaiaobject_ prefixed fields against GAIA_OBJECT_FIELDS directly
  if (dbFieldName.startsWith("gaiaobject_")) {
    if (GAIA_OBJECT_FIELDS[dbFieldName as GaiaObjectFieldKey]) {
      return GAIA_OBJECT_FIELDS[dbFieldName as GaiaObjectFieldKey];
    }
    // Strip prefix and prettify the remainder for unmapped gaiaobject_ fields
    return prettifyCamelCase(
      snakeToCamel(dbFieldName.replace("gaiaobject_", "")),
    );
  }
  const camelCaseField = snakeToCamel(dbFieldName);
  const generalMapping = (fieldName: string): string => {
    return (
      GENERAL_FIELD_MAPPINGS[dbFieldName as GeneralFieldKey] ||
      GENERAL_FIELD_MAPPINGS[fieldName as GeneralFieldKey] ||
      prettifyCamelCase(fieldName)
    );
  };

  switch (category) {
    case "Task details":
      return (
        TASK_FIELD_MAPPINGS[camelCaseField as TaskFieldKey] ||
        generalMapping(camelCaseField)
      );
    case "Progress update":
    case "Audit details":
      return (
        AUDIT_FIELDS[camelCaseField as AuditFieldKey] ||
        generalMapping(camelCaseField)
      );
    case "Notice details":
    case "Litigation details":
      // Check direct field mapping first
      if (GAIA_OBJECT_FIELDS[dbFieldName as GaiaObjectFieldKey]) {
        return GAIA_OBJECT_FIELDS[dbFieldName as GaiaObjectFieldKey];
      }
      // Then check camelCase version
      return (
        GAIA_OBJECT_FIELDS[camelCaseField as GaiaObjectFieldKey] ||
        generalMapping(camelCaseField)
      );
    case "Payment & Assessment":
      return (
        PAYMENT_ASSESSMENT_FIELD_MAPPINGS[
          camelCaseField as PaymentAssessmentFieldKey
        ] || generalMapping(camelCaseField)
      );
    case "Reviews":
      return (
        REVIEW_FIELD_MAPPINGS[dbFieldName as ReviewFieldKey] ||
        REVIEW_FIELD_MAPPINGS[camelCaseField as ReviewFieldKey] ||
        generalMapping(camelCaseField)
      );
    case "Escalation":
      return (
        ESCALATION_FIELD_MAPPINGS[dbFieldName as EscalationFieldKey] ||
        ESCALATION_FIELD_MAPPINGS[camelCaseField as EscalationFieldKey] ||
        generalMapping(camelCaseField)
      );
    default:
      return generalMapping(camelCaseField);
  }
};

export interface ReopenEntry {
  reason: string;
  reopenedBy: string;
  reopenedAt: string;
}

export const parseReopenHistory = (
  oldValue: string,
  newValue: string,
): ReopenEntry | null => {
  const parseEntries = (value: string): Array<Record<string, string>> => {
    if (!value || value === "[]") return [];
    // Format: [{reason=x, reopenedBy=y, reopenedAt=z}, {...}]
    const entries: Array<Record<string, string>> = [];
    const entryRegex = /\{([^}]+)\}/g;
    let match;
    while ((match = entryRegex.exec(value)) !== null) {
      const entry: Record<string, string> = {};
      const parts = match[1].split(/,\s*(?=\w+=)/);
      parts.forEach((part) => {
        const eqIndex = part.indexOf("=");
        if (eqIndex > 0) {
          const key = part.substring(0, eqIndex).trim();
          const val = part.substring(eqIndex + 1).trim();
          entry[key] = val;
        }
      });
      entries.push(entry);
    }
    return entries;
  };

  const newEntries = parseEntries(newValue);
  if (newEntries.length === 0) return null;

  const oldEntries = parseEntries(oldValue);

  const latestEntry = newEntries[newEntries.length - 1];
  if (
    newEntries.length > oldEntries.length &&
    latestEntry.reason !== undefined
  ) {
    return {
      reason: latestEntry.reason || "",
      reopenedBy: latestEntry.reopenedBy || "",
      reopenedAt: latestEntry.reopenedAt || "",
    };
  }

  return null;
};

export const isProgressUpdate = (activity: ActivityItem): boolean => {
  const { resourceId, operation, changeLogs } = activity;
  if (
    !resourceId.startsWith("audit") &&
    !resourceId.startsWith("notice") &&
    !resourceId.startsWith("litigation")
  ) {
    return false;
  }
  return (
    operation === "create" ||
    changeLogs.some((change) =>
      isStatusRelatedChange(change, statusUpdateFields),
    )
  );
};
